import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// EN mirror of /notary — 31 notarial acts hub
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import {
  notaryAggregateOfferSchema,
  notaryOfferCatalogSchema,
  notaryCaseStudiesSchema,
  notarySpeakableSchema,
} from "@/lib/notary-schema";
import { Scale, FileSignature, ShieldCheck, MapPin, Globe2 } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { NOTARY_ACTS } from "@/data/notary-acts";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { SnippetAnswer } from "@/components/seo/SnippetAnswer";
import { areaServedSchema } from "@/data/notary-coverage";
import { NOTARY_NATIONALITIES } from "@/data/notary-nationalities";
import { NOTARY_LOCALES } from "@/data/notary-i18n";
import { NOTARY_PRICE_RANGE } from "@/data/notary-pricing";
import { NOTARY_CASE_STUDIES } from "@/data/notary-case-studies";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import NotaryEmbassyWall from "@/components/NotaryEmbassyWall";
import NotaryQuickContact from "@/components/NotaryQuickContact";
import NotaryCompatChecker from "@/components/NotaryCompatChecker";
import NotaryLanguageSwitcher from "@/components/NotaryLanguageSwitcher";
import NotaryPricingTable from "@/components/NotaryPricingTable";
import NotaryCaseStudies from "@/components/NotaryCaseStudies";
import NotaryAuthorByline from "@/components/NotaryAuthorByline";
import NotaryLiveTicker from "@/components/NotaryLiveTicker";
import RelatedNotaryLinks from "@/components/RelatedNotaryLinks";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";


const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Notary Public Bangkok — 66 Notarial Acts in 8 Categories | NYC";
const DESC =
  "Court-licensed Notarial Services Attorneys in Bangkok. 8 standard categories (Signature, Copy. Talk to a specialist today; no obligation, fast reply.";

const CATEGORIES: Record<string, string> = {
  signature: "1. Signature Notarization",
  copy: "2. Certified True Copy",
  affidavit: "3. Affidavit / Jurat / Oath",
  identity: "4. Identity / Status / Authority",
  contracts: "5. Contracts & Legal Acts",
  translation: "6. Certified Translation",
  facts: "7. Facts & Existence",
  protest: "8. Protest of Instruments",
  corporate: "Corporate Acts (legacy)",
  property: "Property & Real Estate (legacy)",
  intl: "International Documents (legacy)",
};

export const Route = createFileRoute("/en/notary/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("notary", "en", "https://nyclanguageinstitute.com/en/notary") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/notary", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/notary", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/notary", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/notary", "en")},
      { property: "og:url", content: `${SITE}/en/notary` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/notary` },
      ...hreflangLinks("/notary", "/en/notary"),
    ],
    scripts: [speakableSchema(), {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LegalService",
        name: "NYC Legal — Notary Public Bangkok",
        url: `${SITE}/en/notary`,
        areaServed: areaServedSchema(),
        availableLanguage: ["en", "th", "zh", "ja", "ko", "ru", "ar", "de", "fr"],
        inLanguage: "en",
        priceRange: "฿800–฿5,000",
        serviceType: "Notarial Services Attorney",
      }),
    }, {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "Get a document notarized in Bangkok",
        inLanguage: "en",
        totalTime: "P1D",
        estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: "800" },
        step: [
          { "@type": "HowToStep", position: 1, name: "Pick the notarial act", text: "Tell us what you need: signature certification, copy certification, affidavit, POA, corporate resolution, etc.", url: `${SITE}/en/notary` },
          { "@type": "HowToStep", position: 2, name: "Send the document via LINE", text: "Message @nycli with a clear scan or photo. We confirm the act, fee, and signing slot." },
          { "@type": "HowToStep", position: 3, name: "Sign in front of the notarial attorney", text: "Walk in at our Bangkok office (Mon–Sat 09:00–18:00) and sign in person before a court-licensed attorney.", url: `${SITE}/en/notary` },
          { "@type": "HowToStep", position: 4, name: "Receive the notarized document", text: "Most acts ready the same day. Add apostille if the document is going abroad.", url: `${SITE}/en/apostille` },
        ],
      }),
    }, notarySpeakableSchema(`${SITE}/en/notary`),
    notaryAggregateOfferSchema({
      url: `${SITE}/en/notary`,
      priceFromThb: NOTARY_PRICE_RANGE.low,
      priceToThb: NOTARY_PRICE_RANGE.high,
      offerCount: NOTARY_PRICE_RANGE.count,
      inLanguage: "en",
    }),
    notaryOfferCatalogSchema({
      url: `${SITE}/en/notary`,
      inLanguage: "en",
      acts: NOTARY_ACTS.map((a) => ({
        slug: a.slug,
        name: a.nameEn,
        nameAlt: a.nameTh,
        priceFromThb: a.priceFromTh,
        turnaroundIso: "P1D",
      })),
    }),
    notaryCaseStudiesSchema({
      url: `${SITE}/en/notary`,
      inLanguage: "en",
      cases: NOTARY_CASE_STUDIES.map((c) => ({
        slug: c.slug,
        headline: c.headlineEn,
        abstract: c.solutionEn,
        dateISO: c.dateISO,
        nationality: c.nationality,
        destinationCountry: c.destinationCountry,
        actName: c.actName,
      })),
    }),
    entitySameAsSchema(),
    softwareApplicationSchema({ url: `https://nyclanguageinstitute.com/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Bangkok Notary Public tooling for 31 notarial acts." }),
    claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(`https://nyclanguageinstitute.com/en/notary`)),
    
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: EnNotaryHub,
});

function EnNotaryHub() {
  const grouped = Object.keys(CATEGORIES).map((cat) => ({
    cat,
    label: CATEGORIES[cat],
    items: NOTARY_ACTS.filter((a) => a.category === cat),
  }));
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-5xl px-4 pt-6">
        <AIAnswerBox
          question="What does NYC Notary Public in Bangkok cover?"
          answer={`NYC offers ${NOTARY_ACTS.length} notarial acts signed in person by a court-licensed Notarial Services Attorney registered with the Lawyers Council of Thailand — affidavits, powers of attorney, certified copies, signature witnessing. From THB 800, same-day, accepted in 120+ countries via Apostille / embassy legalization.`}
          stats={[
            { label: "Acts", value: `${NOTARY_ACTS.length}` },
            { label: "From", value: "THB 800" },
            { label: "Turnaround", value: "Same-day" },
            { label: "Accepted in", value: "120+ countries" },
          ]}
          sourceUrl="https://nyclanguageinstitute.com/en/notary"
          lang="en"
        />
      </div>
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-start justify-between gap-3">
            <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
            <NotaryLanguageSwitcher current="en" ui="en" />
          </div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Notary Public Bangkok — <span className="text-amber-300">{NOTARY_ACTS.length} acts</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            All notarisations are signed in person by a court-licensed Notarial Services Attorney registered with the
            Lawyers Council of Thailand. Walk-in or by appointment, Mon–Sat 09:00–18:00 ICT.
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Notary Public Bangkok">
              NYC Language Institute in Bangkok offers court-licensed Notary Public services across 31 notarial acts — affidavits, powers of attorney, certified true copies, signature witnessing, corporate resolutions, jurats and acknowledgements. All acts are signed in person by a Notarial Services Attorney registered with the Lawyers Council of Thailand, starting from THB 800 with same-day turnaround. Documents are accepted in 120+ countries via apostille or embassy legalization.
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "Starting price", v: "THB 800 / document" },
              { k: "Turnaround", v: "Same day available" },
              { k: "Notarial acts", v: "31 types" },
              { k: "Signer", v: "Court-licensed Attorney" },
              { k: "Accepted in", v: "120+ countries" },
              { k: "DBD registration", v: "0435567000061" },
            ]} />
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-3">
          {[
            { icon: Scale, t: "Court-licensed", d: "6 attorneys on staff, 100+ years combined experience." },
            { icon: FileSignature, t: "Same-day", d: "Most acts completed in 1 business day. Walk-ins welcome." },
            { icon: ShieldCheck, t: "Embassy-accepted", d: "Used daily by embassies, law firms, and Fortune-500 HR teams." },
          ].map((c) => (
            <article key={c.t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <c.icon className="h-7 w-7 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep">{c.t}</h2>
              <p className="mt-2 text-sm text-slate-600">{c.d}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-6xl space-y-10">
          {grouped.map((g) => (
            <div key={g.cat}>
              <h2 className="text-2xl font-extrabold text-navy-deep">{g.label} ({g.items.length})</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {g.items.map((a) => (
                  <Link key={a.slug} to="/en/notary/$act" params={{ act: a.slug }}
                    className="rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-400 min-h-[44px] focus-visible:ring-2 focus-visible:ring-amber-400">
                    <p className="text-sm font-bold text-navy-deep">{a.nameEn}</p>
                    <p className="mt-1 text-xs text-slate-600">{a.nameTh}</p>
                    <p className="mt-2 text-xs text-amber-700">From ฿{a.priceFromTh.toLocaleString()} · {a.turnaround}</p>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <SnippetAnswer
            question="What is a Notary Public in Thailand and who can certify documents?"
            answer="In Thailand a Notary Public is a Notarial Services Attorney — a lawyer trained and registered with the Lawyers Council of Thailand. NYC Legal has six in-house notarial attorneys serving all 77 provinces of Thailand in 9 languages. Services include affidavits, powers of attorney, certified copies, jurats and acknowledgements, starting from THB 800 with same-day turnaround in Bangkok."
            lang="en"
          />
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link to="/en/notary/coverage" className="group rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 transition hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center gap-2 text-emerald-700">
              <MapPin className="h-5 w-5" />
              <span className="text-[11px] font-bold uppercase tracking-widest">Coverage</span>
            </div>
            <h3 className="mt-2 text-lg font-extrabold text-navy-deep group-hover:text-emerald-700">
              All 77 provinces of Thailand
            </h3>
            <p className="mt-1 text-[12px] text-slate-600">
              4 branch offices · 10 local agents · Remote Signing + EMS courier
            </p>
          </Link>
          <Link to="/en/notary/documents" className="group rounded-2xl border border-sky-200 bg-sky-50/60 p-5 transition hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center gap-2 text-sky-700">
              <MapPin className="h-5 w-5" />
              <span className="text-[11px] font-bold uppercase tracking-widest">Documents</span>
            </div>
            <h3 className="mt-2 text-lg font-extrabold text-navy-deep group-hover:text-sky-700">
              All six document families we certify
            </h3>
            <p className="mt-1 text-[12px] text-slate-600">
              Personal · Education · Financial · Corporate · Property · Specialised — with checklists
            </p>
          </Link>
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
            <div className="flex items-center gap-2 text-amber-700">
              <Globe2 className="h-5 w-5" />
              <span className="text-[11px] font-bold uppercase tracking-widest">By nationality</span>
            </div>
            <h3 className="mt-2 text-lg font-extrabold text-navy-deep">Tailored guides per country</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {NOTARY_NATIONALITIES.map((n) => (
                <Link key={n.slug} to="/en/notary/for/$nationality" params={{ nationality: n.slug }}
                  className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-navy-deep ring-1 ring-amber-200 hover:bg-amber-100">
                  {n.flag} {n.nameEn}
                </Link>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <div className="flex items-center gap-2 text-slate-700">
              <Globe2 className="h-5 w-5" />
              <span className="text-[11px] font-bold uppercase tracking-widest">Languages</span>
            </div>
            <h3 className="mt-2 text-lg font-extrabold text-navy-deep">Available in 9 languages</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Link to="/notary" className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-navy-deep ring-1 ring-slate-300 hover:bg-slate-100">🇹🇭 ไทย</Link>
              {NOTARY_LOCALES.map((l) => (
                <a key={l.locale} href={l.path} className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-navy-deep ring-1 ring-slate-300 hover:bg-slate-100">
                  {l.langName}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          Need translation + notary + apostille bundled? See{" "}
          <Link to="/en/apostille" className="font-bold text-amber-700 hover:underline">/en/apostille</Link>{" "}
          or request a written quote at <Link to="/en/quote" className="font-bold text-amber-700 hover:underline">/en/quote</Link>.
        </div>
      </section>
      <NotaryLiveTicker lang="en" />
      <NotaryAuthorByline lang="en" />
      <NotaryPricingTable lang="en" />
      <NotaryCaseStudies lang="en" />
      <NotaryCompatChecker lang="en" />
      <NotaryEmbassyWall lang="en" />
      <NotaryQuickContact lang="en" />
      <RelatedNotaryLinks context="hub" lang="en" />
      <RelatedComparisons category="notary" locale="en" />

          <ServicePageEnhancer serviceName="Notary Public" locale="en" />

      <PlanningToolsLinks locale="en" />
      </main>
  );
}
